<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router/composables'
import { welfareConfig as rocnovelConfig } from './configs/rocnovel'
import { welfareConfig as ocereadConfig } from './configs/oceread'
import { welfareConfig as ficmoonConfig } from './configs/ficmoon'
import WelfareGuideAddScreen from './components/WelfareGuideAddScreen.vue'
import WelfareGuideOpen from './components/WelfareGuideOpen.vue'
import { api } from '@/api'
import { useLoadingStore, useToastStore, useUserStore } from '@/stores'
import { isAppInstalled, isChrome, isEdge, isInThirdAppBrowser, isSafari, isTiktok } from '@/utils/bowserEnv'
import { isAndroid, isIOS } from '@/constants'
import { TASK_TYPE } from '@/utils/enum'
import { tripartiteLoginPage } from '@/composables/useTripartiteLogin'
import { useElementClick } from '@/composables/tracks/useElementClick'
import { pageViewTrack } from '@/composables/tracks'
import { loadBrandConfig } from '@/utils/loadBrandConfig'

// 🔥 加载当前品牌配置（构建时 tree-shaking）
const config = loadBrandConfig({
  rocnovel: rocnovelConfig,
  oceread: ocereadConfig,
  ficmoon: ficmoonConfig,
})

const { $toast } = useToastStore()
const { $showLoading, $hideLoading } = useLoadingStore()
const { elementClick } = useElementClick()
const userStore = useUserStore()
const route = useRoute()

// 定义背景图 URL 供 CSS v-bind 使用
const homeBgUrl = config.resources.homeBg ? `url(${config.resources.homeBg})` : 'none'
const btnBgColor = config.theme.btnBg

const signed = ref(false)
const progress = ref<any>([])
const modalSigned = ref(false)
const progressInfo = ref<any>()
const tomorrowInfo = ref<any>()
const checkInDayNum = ref(0)
const showTabbar = computed(() => route.query.from !== 'reward')

async function fetchProgress() {
  try {
    const { data } = await api.get<any>('/member/task/signTaskInfo')
    signed.value = data.isSign
    progress.value = data.memberSignResponseList

    // 当前签到信息
    if (signed.value) {
      for (let i = progress.value.length - 1; i >= 0; i--) {
        if (progress.value[i].isSign) {
          progressInfo.value = progress.value[i]
          tomorrowInfo.value = progress.value[i + 1] ?? null
          break
        }
      }
    }
    else {
      for (let i = 0; i < progress.value.length; i++) {
        if (!progress.value[i].isSign) {
          progressInfo.value = progress.value[i]
          tomorrowInfo.value = progress.value[i + 1] ?? null
          break
        }
      }
    }

    // 领取天数
    for (let i = progress.value.length - 1; i >= 0; i--) {
      if (progress.value[i].isSign) {
        checkInDayNum.value = i + 1
        break
      }
    }
    await userStore.init({ force: true })
  }
  catch (error: any) {
    console.error(error)
    $toast(error.msg || 'get check in progress failed')
  }
}

async function onCheckIn() {
  if (signed.value)
    return

  try {
    $showLoading()
    await api.get<any>('/member/task/sign')
    modalSigned.value = true
    pageViewTrack('check_in')
    fetchProgress()
  }
  catch (error: any) {
    console.error(error)
    $toast(error.msg || 'check in failed')
  }
  finally {
    $hideLoading()
    elementClick('check_in')
  }
}

// #region tasks
type ITask = TTaskList & {
  type: string
  status: string
}
const tasks = ref<ITask[]>([])
const showGuidePopupAddHome = ref(false)
const showGuideOpen = ref(false)

const finishPopup = ref(false)
const coins = ref(0)
const completeTaskDescription = ref('')

const showAddTasks = computed(() => {
  const ios = isIOS && (isChrome() || isSafari() || isEdge())
  const android = isAndroid && isChrome()
  return (ios || android) && !isTiktok()
})

async function fatchTasks() {
  try {
    const { data } = await api.post<null, PostTaskListResponse, PostTaskListBody>('/task/list', {})

    // 任务列表
    tasks.value = (data || []).filter(item => (
      item.taskType === 0
      || item.taskType === 1
      || (item.taskType === 2 && showAddTasks.value)
    )) as ITask[]
  }
  catch (error: any) {
    console.error(error)
    $toast(error.msg)
  }
}

const getBtnStatus = function (item: ITask) {
  if (item.taskType === TASK_TYPE.BIND || item.taskType === TASK_TYPE.ADD_BROWSER) {
    if (item.buttonState === 2)
      return 'ok'
    else
      return 'go'
  }

  if (item.taskType === TASK_TYPE.ADD_TO_HOME_SCREEN) {
    if (isAppInstalled())
      return 'ok'
    else
      return 'go'
  }
  return ''
}

function finishTaskTrack(taskType: number) {
  if (taskType === TASK_TYPE.BIND)
    pageViewTrack('task_success_login')

  else if (taskType === TASK_TYPE.ADD_BROWSER)
    pageViewTrack('task_success_browser')

  else if (taskType === TASK_TYPE.ADD_TO_HOME_SCREEN)
    pageViewTrack('task_success_homescreen')
}

async function getCoins(item: ITask) {
  try {
    const { code } = await api.post<null, PostTaskCoinsResponse, PostTaskCoinsBody>('/task/coins', {
      taskConfigDetailId: item.id,
      taskType: item.taskType,
      coins: item.coins,
    })
    if (code === 0) {
      finishTaskTrack(item.taskType)
      finishPopup.value = true
      coins.value = item.coins
      fatchTasks()
      userStore.init({ force: true })
    }
  }
  catch (error: any) {
    console.error(error)
    $toast(error.msg)
  }
}
function handleTask(item: ITask) {
  elementClick(getBtnStatus(item).toUpperCase())

  // 领取
  if (item.buttonState === 2 || (item.taskType === TASK_TYPE.ADD_TO_HOME_SCREEN && isAppInstalled())) {
    getCoins(item)
    completeTaskDescription.value = item.taskTitle
    return
  }

  if (item.taskType === TASK_TYPE.BIND) {
    tripartiteLoginPage('Rewards')
    return
  }

  if (item.taskType === TASK_TYPE.ADD_TO_HOME_SCREEN) {
    showGuidePopupAddHome.value = true
    pageViewTrack('task_go_homescreen')
  }

  if (item.taskType === TASK_TYPE.ADD_BROWSER) {
    showGuideOpen.value = true
    pageViewTrack('task_go_browser')
  }
}

function onCheckInOk() {
  modalSigned.value = false
  if (isInThirdAppBrowser())
    showGuideOpen.value = true
}
// #endregion

onMounted(async () => {
  fetchProgress()
  fatchTasks()
})
</script>

<template>
  <div class="min-h-100dvh pb-20">
    <!-- Cover -->
    <div class="cover w-full relative">
      <div class="flex justify-between items-center px-5 pt-3.5 pb-3">
        <div>
          <h1 class="text-12px text-n-text-2">
            {{ config.texts.myWallet }}
          </h1>
        </div>
        <div class="flex items-center">
          <img class="w-24px h-24px" :src="config.resources.welfareGold">
          <strong class="ml-5px text-16px text-n-text-1">{{ userStore.$state.coinBalance }}</strong>
          <span class="ml-5px text-12px text-n-text-2">{{ config.texts.coins }}</span>
        </div>
      </div>
      <div class="mx-10px px-10px py-16px bg-n-bg-card b-rd-2">
        <h3 class="text-18px text-n-text-1 font-bold">
          {{ config.texts.dailyCheckIn }}
        </h3>
        <p class="text-12px text-n-text-3">
          {{ config.texts.youHaveCheckedIn }} {{ checkInDayNum }} {{ config.texts.dayStraight }}
        </p>
        <div class="mt-15px px-4px grid grid-cols-7 space-x-8px">
          <div
            v-for="(item, index) in progress"
            :key="item.num"
            class="text-center"
            :class="{ 'opacity-70': item.isSign }"
          >
            <div class="flex flex-col items-center pt-10px pb-6px bg-n-bg-tag b-rd-5px">
              <img v-if="index !== progress.length - 1" class="w-24px h-24px" :src="config.resources.welfareGold">
              <img v-else class="w-28px h-25px mb--1px" :src="config.resources.welfareBox">
              <span class="mt-3px text-10px text-n-text-1">+{{ item.prizeNum }}</span>
            </div>
            <div class="flex justify-center">
              <i-assets-icon-right v-if="item.isSign" class="mt-10px text-n-primary" />
              <span v-else class="text-10px text-n-text-3 mt-8px">{{ config.texts.day }} {{ item.num }}</span>
            </div>
          </div>
        </div>
        <div class="flex justify-center">
          <a
            v-if="progress.length"
            class="block mt-4 w-200px h-9 lh-9 text-15px text-white text-center bg-n-primary rounded-full"
            :class="{ 'opacity-70': signed }"
            @click="onCheckIn"
          >
            <span v-if="!signed">{{ config.texts.checkIn }}</span>
            <span v-else-if="checkInDayNum > 0 && checkInDayNum === progress.length">{{ config.texts.tomorrowToGetMore }}</span>
            <span v-else>{{ config.texts.tomorrowCheckIn }} {{ tomorrowInfo?.prizeNum }}</span>
          </a>
        </div>
      </div>

      <div v-if="tasks && tasks.length" class="mt-10px mx-10px px-10px py-16px bg-n-bg-card b-rd-2">
        <h3 class="text-18px text-n-text-1 font-bold">
          {{ config.texts.tasks }}
        </h3>

        <div
          v-for="(item, index) in tasks"
          :key="index"
          class="flex justify-between items-center py-16px px-12px border-b-solid border-b-1px border-n-border-primary last:border-b-0"
        >
          <div>
            <div class="text-n-text-1 font-medium">
              {{ item.taskTitle }}
            </div>
            <div class="text-n-primary text-12px flex items-center mt-4px">
              <img class="w-14px h-14px" :src="config.resources.welfareGold">
              <span class="ml-1" :style="{ color: config.theme.coinsColor }">{{ `+${item.coins}` }}</span>
            </div>
          </div>

          <div
            v-if="item.buttonState !== 1"
            class="w-60px text-center py-4px b-rd-15px text-12px btn-bg"
            :style="{ color: config.theme.btnText }"
            @click="handleTask(item)"
          >
            {{ getBtnStatus(item) }}
          </div>
          <div v-else class="w-60px flex items-center justify-center">
            <i-assets-icon-right class="color-n-primary" />
          </div>
        </div>
      </div>
    </div>

    <WelfareGuideAddScreen v-model="showGuidePopupAddHome" />
    <WelfareGuideOpen v-model="showGuideOpen" />

    <BasicModal v-model="modalSigned">
      <div class="p-5">
        <div class="text-17px lh-2em font-bold text-center text-n-text-1">
          <p>{{ config.texts.checkInGet }} <span class="text-n-primary">{{ progressInfo?.prizeNum }}</span> {{ config.texts.coins }}</p>
        </div>
        <a
          class="block mt-4 w-full h-9 lh-9 text-15px text-white text-center bg-n-primary rounded-full"
          @click="onCheckInOk"
        >
          {{ config.texts.ok }}
        </a>
      </div>
    </BasicModal>

    <BasicModal v-model="finishPopup">
      <div class="p-5">
        <div class="text-17px lh-2em font-bold text-center text-n-text-1">
          <p>{{ completeTaskDescription }} {{ config.texts.get }} <span class="text-n-primary">{{ coins }}</span> {{ config.texts.coins }}</p>
        </div>
        <a
          class="block mt-4 w-full h-9 lh-9 text-15px text-white text-center bg-n-primary rounded-full"
          @click="finishPopup = false"
        >
          {{ config.texts.ok }}
        </a>
      </div>
    </BasicModal>

    <BasicTabbar v-if="showTabbar" tab="rewards" />
  </div>
</template>

<style scoped>
.cover {
  background: v-bind(homeBgUrl) no-repeat top left;
  background-size: 100% auto;
}

.btn-bg {
  background-color: v-bind(btnBgColor);
}
</style>
